import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, ViewChild } from '@angular/core';
import { DataViewer } from '../../../abstract/DataViewer';
import { BeNeo4jService } from '../../../services/be-neo4j.service';
import { SharedService } from '../../../services/shared.service';
import { Exporter } from '../../../clases/Exporter';
import { ShapeBounding } from '../../../clases/ShapeBounding';
import { MainComponent } from '../../../pages/main/main.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { CanvasRenderer } from '../../../abstract/CanvasRenderer';
import { MatButtonModule } from '@angular/material/button';
import { CdkMenuModule } from '@angular/cdk/menu';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { Overlay } from '@angular/cdk/overlay';
import { CanvasItem } from '../../../clases/CanvasItem';
import { generateUUID } from '../../../utils/utils';
import { Values } from '../../../clases/Values';
import { N4JCMAction, Neo4jContextMenuComponent } from '../../neo4j-context-menu/neo4j-context-menu.component';
import { AbstractShape } from '../../../abstract/Shape';
import { Connection } from '../../../clases/Connection';
import { CreateConnectionArrow } from '../../../clases/CreateConnectionArrow';
import { createIConnections, updateCreateConnection } from '../../../utils/ConnectionUtils';
import { DBItem, Record } from '../../../interfaces/Record';
import { ServerResponse } from '../../../interfaces/ServerResponse';
import { Neo4jGeneratorService } from '../../../services/neo4j-generator.service';
import { createIContainers, getShapeWithId } from '../../../utils/ContainerUtils';
import { IView } from '../../../interfaces/View';
import { UserService } from '../../../services/user.service';
import { SaveView } from '../../../clases/SaveView';
import { ViewFromServer } from '../../../interfaces/ViewFromServer';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ISaveView } from '../../../interfaces/SaveView';
import { extractElements, separateNodesFromRelations } from '../../../utils/RecordUtils';
import { NotifyDialogService } from '../../../services/notify-dialog.service';
import { CREATE_VIEW_FROM_CONTAINER, CREATOR_NOTIFICATION, MOVE_NODES_TITLE, MOVE_NODES_WARNING_TEXT } from '../../../shared/strings';
import { CenterCanvasComponent } from '../../buttons/center-canvas/center-canvas.component';
import { TaskNotificationService } from '../../../services/task-notification.service';
import { DataViewActionsComponent } from '../data-view-actions/data-view-actions.component';
import { TaskNotificationBarComponent } from '../../task-notification-bar/task-notification-bar.component';

@Component({
  selector: 'neo4j-creator',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    CdkMenuModule,
    MatMenuModule,
    Neo4jContextMenuComponent,
    MatIconModule,
    MatTooltipModule,
    CenterCanvasComponent,
    MatTooltipModule,
    DataViewActionsComponent,
    TaskNotificationBarComponent
  ],
  providers: [{ provide: DataViewer, useExisting: Neo4jCreatorComponent }],
  templateUrl: './neo4j-creator.component.html',
  styleUrl: './neo4j-creator.component.scss'
})
export class Neo4jCreatorComponent extends CanvasRenderer implements AfterViewInit{

  constructor(
    protected override beNeo4jService: BeNeo4jService,
    protected override sharedService: SharedService,
    private overlay: Overlay,
    private neo4jGeneratorService: Neo4jGeneratorService,
    private userService: UserService,
    private notifyDialogService: NotifyDialogService,
    private taskNotificationService: TaskNotificationService
  ) { super(beNeo4jService, sharedService); }

  CREATOR_NOTIFICATION = CREATOR_NOTIFICATION;

  CREATE_VIEW_STRING = CREATE_VIEW_FROM_CONTAINER;

  viewCreator: Neo4jCreatorComponent = this;

  @ViewChild('canvas', { static: true })
  set canvasRef(value: ElementRef<HTMLCanvasElement>) {
    let canvas: HTMLCanvasElement = value.nativeElement;
    this.canvas = canvas;
    this._canvasBounding = new ShapeBounding(0, 0, canvas.width, canvas.height);
  }

  @Input()
  override set appComponent(value: MainComponent) {
    this._appComponent = value;
  }

  override get appComponent(): MainComponent {
    return this._appComponent;
  }

  @Input()
  set containerElement(value: HTMLElement) {
    this._containerElement = value;
  }

  get containerElement(): HTMLElement {
    return this._containerElement;
  }

  @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;

  @ViewChild(Neo4jContextMenuComponent, { static: true }) menu!: Neo4jContextMenuComponent;

  private readonly contextMenuDragThreshold = 4;
  private contextMenuStartPosition: Values | null = null;

  wasMoved: boolean = false;
  createConnectionArrow!: CreateConnectionArrow | null;
  forConnection!: AbstractShape | null | undefined;

  isNested: boolean = false;

  selectedRelation: string = "";

  lastMousePosition: Values = new Values(0,0);

  ngAfterViewInit(): void {
    this._isViewEditable = true;
    setTimeout(() => {
      this.setupCanvas();
      this.bindEvents();
    }, 0)
    document.addEventListener('contextmenu', (event) => {
      event.preventDefault();
    });
  }

  openMenu(e: MouseEvent) {
    let selectedRelation = this.canvasHelper.checkSelectedConnection(e, this.connections, this.canvasItems, this.currentConnectionFocus, this.currentFocus, this.setConnections);
    if(selectedRelation){
      this.isNested = false;
    }else{
      let seletecShape = this.canvasHelper.checkSelectedShape(e, this.connections, this.canvasItems, this.currentConnectionFocus, this.currentFocus, this.setCanvasItems)
      if(seletecShape){
        this.isNested = true;
      }else{
        return;
      }
    }
    const { x: mX, y: mY } = this.mousePosition;
    let contextMenuPos = new Values(mX, mY);
    this.menu.openMenu(contextMenuPos);
  }

  override onMouseMove(e: MouseEvent): void {
    if(this.camera.isPanning && !this.wasMoved && this.contextMenuStartPosition){
      const deltaX = e.clientX - this.contextMenuStartPosition.x;
      const deltaY = e.clientY - this.contextMenuStartPosition.y;
      this.wasMoved = Math.hypot(deltaX, deltaY) >= this.contextMenuDragThreshold;
    }
    super.onMouseMove(e);
  }

  override onMouseUp(e: MouseEvent): void {
    if(this.currentFocus.getShape().length > 1){
      super.onMouseUp(e);
      this.wasMoved = false;
      this.contextMenuStartPosition = null;
      return;
    }
    if(!this.wasMoved && e.button === 2){
      const { offsetX, offsetY } = e;
      const { x, y } = this.camera.toCanvasCoordinates(offsetX, offsetY);
      this.lastMousePosition = new Values(x, y);
      this.openMenu(e);
      this.wasMoved = false;
    }
    this.changeItemParent();
    super.onMouseUp(e);
    this.wasMoved = false;
    this.contextMenuStartPosition = null;
  }

  override onKeyDown(e: KeyboardEvent) {
    super.onKeyDown(e);
    switch (e.key) {
      case "Escape":
        if(this.createConnectionArrow){
          this.createConnectionArrow = null;
          this.forConnection = null;
          this.currentFocus.resetList();
          this.draw();
        }
        return;
    }
  }

  changeItemParent(){
    let newParent = this.canvasHelper.currentDropIn;
    let toChange = this.currentFocus.getShape()[0];
    if(toChange)toChange.showShadow = false;
    if(!newParent || !toChange) return;
    this.notifyDialogService.openDialog(MOVE_NODES_TITLE, MOVE_NODES_WARNING_TEXT, true, () => {
      this.neo4jGeneratorService.changeNodeFather(
        toChange.dbPropierties.hierarchy,
        newParent.getLabels(),
        newParent.dbPropierties.__EDT_UUID__,
        toChange.getLabels(),
        toChange.dbPropierties.__EDT_UUID__
      ).subscribe({
        next: (response: any) => {
          if (toChange.parent) {
            toChange.parent.children = toChange.parent.children.filter((item: AbstractShape) => item != toChange);
          } else {
            this.canvasItems = this.canvasItems.filter((item: AbstractShape) => item != toChange);
          }
          newParent.addChild(toChange, this.canvasItems);
          this.changeChildPosition(this.mouseCanvas, toChange, newParent);
          this.isShapeHover = true;
          this.canvasHelper.currentDropIn!.setFocus(false);
          this.canvasHelper.currentDropIn = undefined;
          this.assignNewNodes(response.result, [toChange]);
          this.draw();
          this.updateCurrentSchemaTree();
        }
      });
    }, ()=>{
      newParent.setFocus(false);
      this.canvasHelper.currentDropIn = undefined;
      this.currentFocus.resetList();
      this.draw();
    });

  }

  assignNewNodes(records: Record[], item: AbstractShape[]){
    let nodes: DBItem[] = [];

    let elements = extractElements(records);
    separateNodesFromRelations(elements, nodes, []);
    this.updateNodes(nodes, item);
  }

  updateNodes(nodes: DBItem[], items: AbstractShape[]){
    for (const item of items) {
      let node = nodes.find((n: DBItem)=>n.properties.__EDT_UUID__ === item.dbPropierties.__EDT_UUID__);
      if(!node) {
        this.updateNodes(nodes, item.children);
        continue;
      }
      item.setDBData(node);
      this.updateNodes(nodes, item.children);
    }
  }

  override onMouseDown(e: MouseEvent): void{
    if(e.button === 2){
      this.contextMenuStartPosition = new Values(e.clientX, e.clientY);
      this.wasMoved = false;
    }
    super.onMouseDown(e);
    let focusList = this.currentFocus.getShape();
    if(this.createConnectionArrow && focusList.length === 1){
      this.recordRelationship();
    }
  }

  recordRelationship() {
    let focus = this.currentFocus.getShape()[0];
    if (!focus) return;
    if (!this.forConnection) return;
    if (this.forConnection === focus) return;
    this.createConnectionArrow = null;

    this.taskNotificationService.trackObservable(this.neo4jGeneratorService.addFlowRelationship(
      this.forConnection.getLabels(),
      this.forConnection.dbData.properties.__EDT_UUID__,
      focus.getLabels(),
      focus.dbData.properties.__EDT_UUID__
    ),
      this.CREATOR_NOTIFICATION.create_relation, {
      pendingMessage: this.CREATOR_NOTIFICATION.operation_is_being_processed,
      successMessage: this.CREATOR_NOTIFICATION.operation_complete,
      errorMessage: this.CREATOR_NOTIFICATION.operation_error
    }).subscribe({
      next: (response: ServerResponse<Record>) => {
        let key = response.result[0].keys[0];
        let relation = response.result[0].fields[key];
        let connection = new Connection(relation.properties.__EDT_UUID__, this.forConnection!.id, focus.id, 0, 0, 0, 0, this.forConnection!, focus, relation.type, "");
        connection.setRelation(relation);
        this.connections.push(connection);
        this.forConnection = null;
        this.draw();
      }
    });
  }

  deleteFlowRelation(){
    let focus = this.currentConnectionFocus.getConnection();
    if (!focus) return;
    let originalFrom = getShapeWithId(focus.from, this.canvasItems);
    let originalTo = getShapeWithId(focus.to, this.canvasItems);
    if(!originalFrom || !originalTo) return;
    this.notifyDialogService.openDialog("Confirm Deletion", "Are you sure you want to delete this relation? It will be permanently removed from the database.", true, ()=>{
      this.taskNotificationService.trackObservable(this.neo4jGeneratorService.removeFlowRelationship(
        originalFrom!.getLabels(),
        originalFrom!.dbData.properties.__EDT_UUID__,
        originalTo!.getLabels(),
        originalTo!.dbData.properties.__EDT_UUID__,
        focus.type!,
        focus.dbPropierties.__EDT_UUID__
      ),
        this.CREATOR_NOTIFICATION.delete_relation, {
        pendingMessage: this.CREATOR_NOTIFICATION.operation_is_being_processed,
        successMessage: this.CREATOR_NOTIFICATION.operation_complete,
        errorMessage: this.CREATOR_NOTIFICATION.operation_error
      }).subscribe({
        next: (response: ServerResponse<Record>) => {
          this.deleteRelationship(focus);
        }
      });
    }); 
  }

  createHierachyName(name: string){
    return name.toUpperCase().replaceAll(" ", "_");
  }

  override draw(): void {
    super.draw();
    if (!this.ctx) return;
    this.ctx.save();

    // Apply camera transformations
    this.ctx.translate(this.camera.offsetX, this.camera.offsetY);
    this.ctx.scale(this.camera.scale, this.camera.scale);

    if(this.createConnectionArrow){
      updateCreateConnection(this.createConnectionArrow, this.mouseCanvas, this.canvasItems);
      this.createConnectionArrow.draw(this.ctx);
    }

    this.ctx.restore();
  }

  protected override drawOnTop(ctx: CanvasRenderingContext2D) {
    for (const items of this.canvasItems) {
      items.drawOnTop(ctx);
    }
  }

  makeAction = (response: any) =>{
    switch(response.type){
      case N4JCMAction.CREATE_NODE:
        if(this.isNested) {
          this.createNested(response.nodeName, response.labelName);
          this.isNested = false;
          return;
        }
        this.createNode(response.nodeName, response.labelName);
        break;
      case N4JCMAction.DELETE_NODE:
        this.deleteNode(response);
        break;
      case N4JCMAction.CREATE_RELATIONSHIP:
        this.createRelationship(response);
        break;
      case N4JCMAction.DELETE_RELATIONSHIP:
        this.deleteFlowRelation();
        break;
      case N4JCMAction.SAVE_CONTAINER_AS_VIEW:
        this.toSaveAsView(response);
        break;
      case N4JCMAction.REMOVE_FROM_VIEW:
        this.removeNodeFromView(response);
        break;
      case N4JCMAction.REMOVE_ONLY_THIS_FROM_VIEW:
        this.removeOnlyThisFromView(response);
        break;
    }
  }

  removeNodeFromView(response: any){
    this.notifyDialogService.openDialog("Confirm Removal", "The node and all its children will only be removed from the view. They will continue to exist in the database.", true, ()=>{
      let toRemove = (response.node as AbstractShape);
      let index = this.canvasItems.findIndex((item: AbstractShape)=> item === toRemove);
      let list = toRemove.parent?.children || this.canvasItems;
      list.splice(index, 1);
      this.draw();
    })
  }

  removeOnlyThisFromView(response: any){
    this.notifyDialogService.openDialog("Confirm Removal", "Only this node will only be removed from the view, keeping the children outside any container. They will continue to exist in the database.", true, ()=>{
      let toRemove = (response.node as AbstractShape);
      let list = toRemove.parent?.children || this.canvasItems;
      let index = list.findIndex((item: AbstractShape)=> item === toRemove);
      let children = [...toRemove.children];
      let offsets = toRemove.calculateAllOffsets(this.canvasItems);
      let removedAbsoluteX = offsets.x + toRemove.x;
      let removedAbsoluteY = offsets.y + toRemove.y;
      list.splice(index, 1);
      for (const child of children){
        let childX = child.x;
        let childY = child.y;
        child.setParent(undefined);
        child.setPosition(childX + removedAbsoluteX, childY + removedAbsoluteY);
        this.canvasItems.push(child);
      }
      toRemove.children = [];
      this.draw();
    })
  }

  toSaveAsView(response: any){
    let toSave = (response.node as AbstractShape);
    let containers = createIContainers([toSave], this.canvasItems);
    let hierarchy = toSave.dbPropierties.hierarchy;
    let conn = this.connections.filter((connection: Connection)=> connection.dbPropierties.name === hierarchy);
    let connections = createIConnections(conn, this.connections);
    this.isLoading = true;
    this.beNeo4jService.getCypherById(toSave.dbPropierties.__EDT_UUID__, hierarchy, true).subscribe({
      next: (result: any)=>{
        let cypher = result.result;
        let containerView: IView = { view: containers, connections: connections, uuid: generateUUID(), sentence: cypher } as IView;
        let importDocument = {
          data_service_id: this.savedDocument.connection.dataService.data_service_id,
          containers: [containerView],
          name_value: this.savedDocument.name_value
        } as ISaveView;
        let saveView = new SaveView(response.name, JSON.stringify(importDocument), hierarchy);
        this.userService.saveView(saveView).subscribe({
          next: (view: ServerResponse<ViewFromServer>)=>{
            this.isLoading = false;
            this.appComponent.viewsOverview.getViews();
            this.notifyDialogService.openDialog(this.CREATE_VIEW_STRING.title, this.CREATE_VIEW_STRING.content);
          },
          error: (err)=>{
            this.isLoading = false;
          }
        });
      },
      error: (err)=>{
        this.isLoading = false;
      },
    });
  }

  deleteNode(response: any) {
    this.notifyDialogService.openDialog(
      "Confirm Deletion", 
      "Are you sure you want to delete this node? If the node is part of a view, the view will stop working. All child nodes will also be deleted, and this node will be permanently removed from the database",
      true,
      ()=>{
        let toDelete = (response.node as AbstractShape);
        this.taskNotificationService.trackObservable(this.neo4jGeneratorService.deleteNode(toDelete.getLabels(), toDelete.dbData.properties.__EDT_UUID__),
          this.CREATOR_NOTIFICATION.delete_node, {
          pendingMessage: this.CREATOR_NOTIFICATION.operation_is_being_processed,
          successMessage: this.CREATOR_NOTIFICATION.operation_complete,
          errorMessage: this.CREATOR_NOTIFICATION.operation_error
        }).subscribe({
          next: (response: ServerResponse<Record>) => {
            let parent = toDelete.parent;
            if (parent) {
              parent.children = parent.children.filter((child: AbstractShape) => child.id != toDelete.id);
              this.draw();
              this.updateCurrentSchemaTree();
              return;
            }
            this.canvasItems = this.canvasItems.filter((child: AbstractShape) => child.id != toDelete.id);
            this.draw();
            this.updateCurrentSchemaTree();
          }
        });
      }
    );
  }

  createRelationship(response: any){
    this.selectedRelation = response.relation;
    this.createConnectionArrow = new CreateConnectionArrow(this.currentFocus, this.mouseCanvas.x, this.mouseCanvas.y);
    this.forConnection = this.currentFocus.getShape()[0];
    this.currentFocus.resetList();
  }

  createNode = (name: string, label: string)=>{
    let hierarchy = this.createHierachyName(name);
    this.taskNotificationService.trackObservable(this.neo4jGeneratorService.createNode(name, hierarchy),
      this.CREATOR_NOTIFICATION.creating_node, {
      pendingMessage: this.CREATOR_NOTIFICATION.operation_is_being_processed,
      successMessage: this.CREATOR_NOTIFICATION.operation_complete,
      errorMessage: this.CREATOR_NOTIFICATION.operation_error
    }).subscribe({
      next: (response: ServerResponse<Record>) => {
        let key = response.result[0].keys[0];
        let node = response.result[0].fields[key];
        let item = new CanvasItem(generateUUID(), 0, undefined);
        item.setDBData(node);
        let { x, y } = this.mouseCanvas;
        item.setPosition(x, y);
        this.canvasItems.push(item);
        this.draw();
      }
    });
  }

  createNested = (name: string, label: string) => {
    let focus = this.currentFocus.getShape()[0];
    if(!focus)return;
    this.taskNotificationService.trackObservable(this.neo4jGeneratorService.createNestedNode(focus.getLabels(), focus.dbData.properties.__EDT_UUID__, name),
      this.CREATOR_NOTIFICATION.creating_node, {
      pendingMessage: this.CREATOR_NOTIFICATION.operation_is_being_processed,
      successMessage: this.CREATOR_NOTIFICATION.operation_complete,
      errorMessage: this.CREATOR_NOTIFICATION.operation_error
    }).subscribe({
      next: (response: ServerResponse<Record>) => {
        let key = response.result[0].keys[0];
        let node = response.result[0].fields[key];
        let item = new CanvasItem(generateUUID(), 0, undefined);
        item.setDBData(node);
        item.setSize(100,100);
        focus.addChild(item, this.canvasItems);
        this.changeChildPosition(this.lastMousePosition, item, focus);
        this.draw();
        this.updateCurrentSchemaTree();
      }
    });
  }

  changeChildPosition(position: Values, item: AbstractShape, parent: AbstractShape) {
    let { x, y } = position;
    let offsets = parent.calculateAllOffsets(this.canvasItems);
    offsets.x = offsets.x + parent.x;
    offsets.y = offsets.y + parent.y;
    let newX = x - offsets.x;
    let newY = y - offsets.y;
    item.setPosition(newX, newY);
  }

  protected override errorReporting(title: string, message: string): void {
    this.notifyDialogService.openDialog(title, message);
  }

  protected override afterFetchingContent(): void {
    super.afterFetchingContent();
    if (this.savedDocument.model_UUID) this.showMetricsButton = true;
  }
}
